/**
 * URL utility functions for UnifiedParsingModal
 */

/**
 * Check if string is a phone number
 */
export const isPhoneNumber = (str: string): boolean => {
  // Remove common phone separators
  const cleaned = str.replace(/[\s\-\(\)\.]/g, '')

  // Check if it's a phone number pattern
  // Starts with + or digit, contains mostly digits, length 10-15
  const phonePattern = /^[\+]?[0-9]{10,15}$/
  return phonePattern.test(cleaned)
}

/**
 * Extract domain from URL
 */
export const extractDomainFromUrl = (url: string): string | null => {
  try {
    // Clean up URL
    let cleanUrl = url.trim()
    if (!cleanUrl) return null

    // Skip if it looks like a phone number
    if (isPhoneNumber(cleanUrl)) {
      return null
    }

    // Add protocol if missing
    if (!cleanUrl.startsWith('http://') && !cleanUrl.startsWith('https://')) {
      cleanUrl = 'https://' + cleanUrl
    }

    const urlObj = new URL(cleanUrl)
    let host = urlObj.hostname

    // Remove www. prefix if present
    if (host.startsWith('www.')) {
      host = host.substring(4)
    }

    return host || null
  } catch {
    // If URL parsing fails, try to extract domain manually
    const match = url.match(/(?:https?:\/\/)?(?:www\.)?([^\/\s?#]+)/i)
    if (match && match[1]) {
      let domain = match[1]

      // Double-check it's not a phone number
      if (isPhoneNumber(domain)) {
        return null
      }

      if (domain.startsWith('www.')) {
        domain = domain.substring(4)
      }
      return domain
    }
    return null
  }
}

/**
 * Normalize URL for comparison (decode, remove trailing slash, lowercase)
 */
export const normalizeUrl = (url: string): string => {
  try {
    return decodeURIComponent(url.replace(/\+/g, '%20')).replace(/\/$/, '').toLowerCase()
  } catch {
    return url.replace(/\+/g, ' ').replace(/\/$/, '').toLowerCase()
  }
}

/**
 * Extract base domain (without www, m, mobile subdomains) + path for fallback matching
 */
export const extractBaseDomainPath = (url: string): string => {
  try {
    const u = new URL(url.startsWith('http') ? url : `https://${url}`)
    // Remove common subdomains: www, m, mobile, wap
    const domain = u.hostname.replace(/^(www\.|m\.|mobile\.|wap\.)/i, '')
    return (domain + u.pathname).replace(/\/$/, '').toLowerCase()
  } catch {
    return url.replace(/^https?:\/\/(www\.|m\.|mobile\.|wap\.)?/i, '').replace(/\/$/, '').toLowerCase()
  }
}

/**
 * Transform URL data for display (extract domain and path)
 */
export const transformUrlForDisplay = (urlData: { url?: string; full_url?: string; domain?: string }): {
  full_url: string
  domain: string
  path: string
} => {
  const fullUrl = urlData.url || urlData.full_url || ''
  let domain = urlData.domain || ''
  let path = ''

  try {
    const urlObj = new URL(fullUrl.startsWith('http') ? fullUrl : `https://${fullUrl}`)
    domain = domain || urlObj.hostname.replace(/^www\./, '')
    path = urlObj.pathname + urlObj.search + urlObj.hash
    if (path === '/') path = ''
  } catch {
    // Keep original values if URL parsing fails
  }

  return {
    full_url: fullUrl,
    domain,
    path: path || '/',
  }
}

/**
 * Check if item is a pure domain (no significant path) or a page (with path)
 */
export const isPureDomain = (fullUrl?: string): boolean => {
  if (!fullUrl) return true

  try {
    const url = new URL(fullUrl)
    // Pure domain has pathname of '/' or empty
    return url.pathname === '/' || url.pathname === ''
  } catch {
    // If parsing fails, treat as domain
    return true
  }
}
